/**
 * Vega charts used by the default dashboard tiles. The classic dial layout is a viewer preference
 * (see `useDashboardTileStyle`); each card's main chart is its own `chart_type`.
 *
 * The spec itself comes from the backend (`dashboards/charts/chart_specs.py`). This file only sizes it to the tile.
 * The assurance pills in the hover are `formatAssuranceChartTooltip`, shared with chat.
 */
import React from 'react';
import Pill from 'src/components/ui/Pill';
import { formatAssuranceChartTooltip, VegaChart, type ChartSpec } from 'src/components/charts';
import { ASSURANCE_COLORS, getTextColorForBg, type AssuranceLevel } from 'src/features/reporting/utils/color';

/** Size a Vega spec to a flex parent so tiles can share one locked height. */
function useFillHeight(fill: boolean, fallback: number) {
  const ref = React.useRef<HTMLDivElement>(null);
  const [measured, setMeasured] = React.useState<number | null>(null);

  React.useLayoutEffect(() => {
    if (!fill) {
      setMeasured(null);
      return;
    }
    const el = ref.current;
    if (!el) return;
    const update = () => {
      const next = Math.round(el.getBoundingClientRect().height);
      if (next > 0) setMeasured(next);
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [fill]);

  return { ref, height: fill ? Math.max(measured ?? fallback, 64) : fallback };
}

export const AssuranceLevelPill: React.FC<{ level: AssuranceLevel }> = ({ level }) => {
  if (level === 'NaN') return null;
  const bg = ASSURANCE_COLORS[level];
  return (
    <Pill
      size="sm"
      className="!text-xs !font-semibold !rounded-full flex-shrink-0"
      style={{ backgroundColor: bg, color: getTextColorForBg(bg) }}
    >
      {level}
    </Pill>
  );
};

/**
 * Draw a backend line or bar spec, sized to this container.
 * Always overrides `height` and `autosize`. Dashboard tiles and the chat
 * popover both render through this.
 */
export const TileChart: React.FC<{
  spec: ChartSpec;
  height?: number;
  fill?: boolean;
  ariaLabel: string;
}> = ({ spec, height = 96, fill = false, ariaLabel }) => {
  const { ref, height: chartHeight } = useFillHeight(fill, height);
  const sized = React.useMemo<ChartSpec>(
    () => ({
      ...spec,
      height: Math.max(chartHeight - 8, 64),
      autosize: { type: 'fit', contains: 'padding' }
    }),
    [spec, chartHeight]
  );

  return (
    <div
      ref={ref}
      className={`w-full overflow-hidden ${fill ? 'h-full min-h-[64px]' : ''}`}
      style={fill ? undefined : { height }}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <VegaChart spec={sized} ariaLabel={ariaLabel} formatTooltip={formatAssuranceChartTooltip} />
    </div>
  );
};
